import React from 'react';
import { motion } from 'framer-motion';
import { Waves, Sparkles, ShoppingBag, Info, Scissors, Phone } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Services = () => {
  const services = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Oyster Bar Móvil",
      description: "El primer Oyster Bar Móvil de Argentina. Llevamos la experiencia gastronómica más exclusiva a tu evento.",
      color: "ocean",
      features: ["Servicio a domicilio", "Experiencias únicas", "Personal especializado"]
    },
    {
      icon: <Waves className="w-8 h-8" />,
      title: "Experiencias",
      description: "Creamos momentos inolvidables con ostras y mariscos vivos. Cada evento es una celebración del mar.",
      color: "jade",
      features: ["Eventos privados", "Celebraciones", "Degustaciones"]
    },
    {
      icon: <ShoppingBag className="w-8 h-8" />,
      title: "Productos",
      description: "Ostras y mariscos vivos de la Patagonia. Calidad premium directo del mar a tu hogar.",
      color: "seagreen",
      features: ["Productos frescos", "Entrega a domicilio", "Calidad garantizada"]
    },
    {
      icon: <Info className="w-8 h-8" />,
      title: "Info Ostras",
      description: "Todo lo que necesitás saber sobre nuestras ostras: origen, calidad y propiedades únicas.",
      color: "pearl",
      features: ["Origen patagónico", "Información detallada", "Guías de degustación"]
    },
    {
      icon: <Scissors className="w-8 h-8" />,
      title: "Cómo abrir",
      description: "Aprende la técnica correcta para abrir ostras como un profesional. Tips y consejos de expertos.",
      color: "coral",
      features: ["Técnicas profesionales", "Consejos prácticos", "Seguridad alimentaria"]
    },
    {
      icon: <Phone className="w-8 h-8" />,
      title: "Cómo pedir",
      description: "Proceso simple y rápido para realizar tu pedido. Atención personalizada y entrega garantizada.",
      color: "silver",
      features: ["Pedidos fáciles", "Atención personalizada", "Entrega rápida"]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0
    }
  };

  return (
    <section id="servicios" className="py-20 bg-deep-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.h2 
            className="text-4xl md:text-5xl font-title font-bold text-ocean-500 mb-4"
            animate={{ 
              backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] 
            }}
            transition={{ 
              duration: 8, 
              repeat: Infinity, 
              ease: "linear" 
            }}
            style={{
              background: "linear-gradient(90deg, #2563eb, #0891b2, #2563eb)",
              backgroundSize: "200% 100%",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}
          >
            Nuestros Servicios
          </motion.h2>
          <motion.p 
            className="text-xl text-pearl-700 max-w-3xl mx-auto font-body"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Descubrí todo lo que Mar de Jade Hogar tiene para ofrecerte. 
            Desde productos frescos hasta experiencias gastronómicas únicas.
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {services.map((service, index) => {
            // Define color classes based on service.color
            const colorClasses = {
              ocean: {
                border: 'border-ocean-500/30 hover:border-ocean-500',
                bgGradient: 'from-ocean-500/0 to-ocean-500/0 group-hover:from-ocean-500/10 group-hover:to-ocean-500/5',
                iconBg: 'bg-deep-200 text-ocean-500 group-hover:bg-deep-100',
                title: 'text-ocean-500 group-hover:text-ocean-400',
                text: 'text-pearl-700',
                dot: 'bg-ocean-500'
              },
              jade: {
                border: 'border-jade-500/30 hover:border-jade-500',
                bgGradient: 'from-jade-500/0 to-jade-500/0 group-hover:from-jade-500/10 group-hover:to-jade-500/5',
                iconBg: 'bg-deep-200 text-jade-500 group-hover:bg-deep-100',
                title: 'text-jade-500 group-hover:text-jade-400',
                text: 'text-pearl-700',
                dot: 'bg-jade-500'
              },
              seagreen: {
                border: 'border-ocean-500/30 hover:border-ocean-500',
                bgGradient: 'from-ocean-500/0 to-jade-500/0 group-hover:from-ocean-500/10 group-hover:to-jade-500/5',
                iconBg: 'bg-deep-200 text-jade-500 group-hover:bg-deep-100',
                title: 'text-ocean-500 group-hover:text-jade-500',
                text: 'text-pearl-700',
                dot: 'bg-jade-500'
              }
            };

            const colors = colorClasses[service.color as keyof typeof colorClasses] || colorClasses.ocean;

            return (
              <motion.div 
                key={index}
                variants={cardVariants}
                whileHover={{ 
                  y: -8, 
                  scale: 1.02,
                  transition: { duration: 0.4, ease: "easeOut" } 
                }}
                className="group"
              >
                <Card className={`h-full bg-deep-200/60 backdrop-blur-sm ${colors.border} transition-all duration-500 shadow-lg hover:shadow-2xl hover:bg-deep-200/70 rounded-2xl overflow-hidden`}>
                  <div className={`absolute inset-0 bg-gradient-to-br ${colors.bgGradient} transition-all duration-500`} />
                  <CardHeader className="text-center pb-4 relative z-10">
                    <motion.div 
                      className={`inline-flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full ${colors.iconBg} transition-colors duration-500`}
                      whileHover={{ 
                        scale: 1.1, 
                        rotate: 5 
                      }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      {service.icon}
                    </motion.div>
                    <CardTitle className={`text-xl font-title font-semibold ${colors.title} mb-2 transition-colors duration-300`}>
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center relative z-10">
                    <CardDescription className="text-gray-600 font-body mb-4 leading-relaxed">
                      {service.description}
                    </CardDescription>
                    <ul className="space-y-2">
                      {service.features.map((feature, featureIndex) => (
                        <motion.li 
                          key={featureIndex}
                          className={`text-sm ${colors.text} font-body flex items-center justify-center gap-2`}
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.5, delay: featureIndex * 0.1 }}
                          viewport={{ once: true }}
                        >
                          <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                          {feature}
                        </motion.li>
                      ))}
                    </ul>
                    
                    {/* Enlace especial para Info Ostras */}
                    {service.title === "Info Ostras" && (
                      <motion.div 
                        className="mt-6"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        viewport={{ once: true }}
                      >
                        <motion.a 
                          href="https://www.instagram.com/stories/highlights/18169416559356005/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${colors.iconBg} hover:shadow-lg`}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.44z"/>
                          </svg>
                          Ver en Instagram
                        </motion.a>
                      </motion.div>
                    )}

                    {/* Enlace especial para Cómo abrir */}
                    {service.title === "Cómo abrir" && (
                      <motion.div 
                        className="mt-6"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        viewport={{ once: true }}
                      >
                        <motion.a 
                          href="https://www.instagram.com/stories/highlights/18196580368309839/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${colors.iconBg} hover:shadow-lg`}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.44z"/>
                          </svg>
                          Ver en Instagram
                        </motion.a>
                      </motion.div>
                    )}

                    {/* Enlace especial para Cómo pedir */}
                    {service.title === "Cómo pedir" && (
                      <motion.div 
                        className="mt-6"
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                        viewport={{ once: true }}
                      >
                        <motion.a 
                          href="https://www.instagram.com/stories/highlights/17856288804453253/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${colors.iconBg} hover:shadow-lg`}
                          whileHover={{ scale: 1.05, y: -2 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.40s-.644-1.44-1.439-1.40z"/>
                          </svg>
                          Ver en Instagram
                        </motion.a>
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          className="text-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.a 
            href="https://wa.me/5491160065484" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-ocean-600 hover:bg-ocean-700 text-white px-8 py-4 rounded-full font-body font-semibold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            <Phone className="w-5 h-5" />
            <span>Consultá por cualquier servicio</span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
